import { useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Academy } from '@court-sync/shared';
import { academyService } from '../services/academy';
import { useAuthStore } from '../store/auth';
import { STALE_TIME, queryClient } from '../queryClient';
import { queryKeys, userAcademiesQueryKey } from './keys';
import { unwrapApiData, unwrapApiDataOrEmpty } from './unwrap';

export function getUserAcademiesFromCache(): Academy[] {
  return queryClient.getQueryData<Academy[]>(queryKeys.academies.user()) ?? [];
}

export function getAcademyFromCache(id: string): Academy | undefined {
  return queryClient.getQueryData<Academy>(queryKeys.academies.detail(id));
}

export async function fetchUserAcademiesQuery(options?: { force?: boolean }): Promise<Academy[]> {
  const key = queryKeys.academies.user();
  if (options?.force) {
    await queryClient.invalidateQueries({ queryKey: key });
  }
  return queryClient.fetchQuery({
    queryKey: key,
    queryFn: async () => {
      const response = await academyService.getUserAcademies();
      return unwrapApiDataOrEmpty(response);
    },
    staleTime: STALE_TIME.MEDIUM,
  });
}

export async function fetchAcademyQuery(
  id: string,
  options?: { force?: boolean }
): Promise<Academy | null> {
  const key = queryKeys.academies.detail(id);
  if (options?.force) {
    await queryClient.invalidateQueries({ queryKey: key });
  }
  return queryClient.fetchQuery({
    queryKey: key,
    queryFn: async () => {
      const response = await academyService.getAcademy(id);
      return unwrapApiData(response, 'Failed to load academy');
    },
    staleTime: STALE_TIME.MEDIUM,
  });
}

export function useUserAcademies(options?: { enabled?: boolean }) {
  const queryClient = useQueryClient();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated && s.isInitialized);

  const { data: academies = [], isLoading, error, refetch } = useQuery({
    queryKey: userAcademiesQueryKey,
    queryFn: async () => {
      const response = await academyService.getUserAcademies();
      return unwrapApiDataOrEmpty(response);
    },
    staleTime: STALE_TIME.MEDIUM,
    enabled: (options?.enabled ?? true) && isAuthenticated,
  });

  const refresh = useCallback(
    async (opts?: { force?: boolean }) => {
      if (opts?.force) {
        await queryClient.invalidateQueries({ queryKey: userAcademiesQueryKey });
      }
      return refetch();
    },
    [queryClient, refetch]
  );

  return {
    academies,
    isLoading,
    error: error instanceof Error ? error.message : error ? String(error) : null,
    refresh,
  };
}

export function useAcademy(academyId?: string | null, options?: { enabled?: boolean }) {
  const queryKey = queryKeys.academies.detail(academyId ?? '');

  const { data: academy = null, isLoading, error, refetch } = useQuery({
    queryKey,
    queryFn: async () => {
      const response = await academyService.getAcademy(academyId!);
      return unwrapApiData(response, 'Failed to load academy');
    },
    enabled: !!academyId && (options?.enabled ?? true),
    staleTime: STALE_TIME.MEDIUM,
  });

  const refresh = useCallback(
    async (opts?: { force?: boolean }) => {
      if (!academyId) return undefined;
      if (opts?.force) {
        await queryClient.invalidateQueries({ queryKey });
      }
      return refetch();
    },
    [academyId, queryKey, refetch]
  );

  return {
    academy,
    isLoading,
    error: error instanceof Error ? error.message : error ? String(error) : null,
    refresh,
  };
}

export function useUpdateAcademy() {
  const queryClient = useQueryClient();
  const userKey = queryKeys.academies.user();

  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string;
      data: Partial<Omit<Academy, 'id' | 'createdAt' | 'updatedAt'>>;
    }) => updateAcademyQuery(id, data),
    onMutate: async ({ id, data }) => {
      const detailKey = queryKeys.academies.detail(id);
      await queryClient.cancelQueries({ queryKey: userKey });
      await queryClient.cancelQueries({ queryKey: detailKey });

      const previousList = queryClient.getQueryData<Academy[]>(userKey);
      const previousDetail = queryClient.getQueryData<Academy>(detailKey);
      const existing = previousDetail ?? previousList?.find((a) => a.id === id);

      if (existing) {
        const optimistic: Academy = { ...existing, ...data, updatedAt: new Date() };
        queryClient.setQueryData<Academy>(detailKey, optimistic);
        queryClient.setQueryData<Academy[]>(userKey, (old) =>
          old?.map((a) => (a.id === id ? optimistic : a))
        );
      }

      return { previousList, previousDetail, detailKey };
    },
    onError: (_err, _vars, context) => {
      if (context?.previousList) {
        queryClient.setQueryData(userKey, context.previousList);
      }
      if (context?.previousDetail !== undefined && context?.detailKey) {
        queryClient.setQueryData(context.detailKey, context.previousDetail);
      }
    },
    onSuccess: (updated) => {
      if (!updated) return;
      queryClient.setQueryData(queryKeys.academies.detail(updated.id), updated);
      queryClient.setQueryData<Academy[]>(userKey, (old) =>
        old?.map((a) => (a.id === updated.id ? updated : a))
      );
    },
  });
}

/** Imperative academy update (for callbacks outside React hooks). */
export async function updateAcademyQuery(
  id: string,
  data: Partial<Omit<Academy, 'id' | 'createdAt' | 'updatedAt'>>
): Promise<Academy | null> {
  const userKey = queryKeys.academies.user();
  const detailKey = queryKeys.academies.detail(id);
  const previousList = queryClient.getQueryData<Academy[]>(userKey);
  const previousDetail = queryClient.getQueryData<Academy>(detailKey);
  const existing = previousDetail ?? previousList?.find((a) => a.id === id);

  if (existing) {
    const optimistic: Academy = { ...existing, ...data, updatedAt: new Date() };
    queryClient.setQueryData<Academy>(detailKey, optimistic);
    queryClient.setQueryData<Academy[]>(userKey, (old) =>
      old?.map((a) => (a.id === id ? optimistic : a))
    );
  }

  try {
    await academyService.updateAcademy(id, data);
    const updated = await fetchAcademyQuery(id, { force: true });
    if (updated) {
      queryClient.setQueryData(detailKey, updated);
      queryClient.setQueryData<Academy[]>(userKey, (old) =>
        old?.map((a) => (a.id === id ? updated : a))
      );
    }
    return updated;
  } catch (error) {
    if (previousList) queryClient.setQueryData(userKey, previousList);
    if (previousDetail !== undefined) queryClient.setQueryData(detailKey, previousDetail);
    throw error;
  }
}

